<template>
  <div id="app" class="d-flex flex-column min-vh-100">
    <!-- Navigation -->
    <nav class="navbar navbar-expand-lg">
      <div class="container-fluid">
        <router-link class="navbar-brand" to="/">
          <i class="bi bi-satellite-dish me-2"></i>
          MyGeotab Feeder
        </router-link>

        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <div class="collapse navbar-collapse" id="navbarNav">
          <!-- Solo mostrar menú si el usuario NO es de cliente -->
          <ul class="navbar-nav me-auto" v-if="!isClientUser">
            <li class="nav-item">
              <router-link class="nav-link" to="/clients" active-class="active">
                <i class="bi bi-people me-1"></i>
                Clientes
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/scripts" active-class="active">
                <i class="bi bi-play-circle me-1"></i>
                Scripts
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/roles" active-class="active">
                <i class="bi bi-shield-lock me-1"></i>
                Roles
              </router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/users" active-class="active">
                <i class="bi bi-people me-1"></i>
                Usuarios
              </router-link>
            </li>
          </ul>

          <!-- Mostrar nombre del cliente si es usuario de cliente -->
          <div class="navbar-text me-auto" v-if="isClientUser">
            <i class="bi bi-building me-2"></i>
            <strong>{{ authStore.user?.client_name || 'Cliente' }}</strong>
          </div>

          <ul class="navbar-nav">
            <li class="nav-item">
              <button
                @click="toggleTheme"
                class="btn nav-theme-toggle me-3"
                :title="themeStore.getOppositeThemeName()"
                aria-label="Cambiar tema"
              >
                <i
                  class="bi me-1"
                  :class="themeStore.isDark() ? 'bi-sun-fill' : 'bi-moon-fill'">
                </i>
                <span class="d-none d-md-inline">
                  {{ themeStore.isDark() ? 'Claro' : 'Oscuro' }}
                </span>
              </button>
            </li>

            <!-- User Dropdown -->
            <li class="nav-item dropdown" v-if="authStore.isAuthenticated">
              <a
                class="nav-link dropdown-toggle user-dropdown"
                href="#"
                id="userDropdown"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i class="bi bi-person-circle me-2"></i>
                <span class="d-none d-md-inline">{{ authStore.userName }}</span>
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li class="dropdown-header">
                  <div class="user-info">
                    <strong>{{ authStore.user?.fullname || 'Usuario' }}</strong>
                    <small class="d-block text-muted">{{ authStore.user?.email }}</small>
                    <small v-if="authStore.userRole" class="badge bg-primary mt-1">
                      {{ authStore.userRole }}
                    </small>
                  </div>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <router-link class="dropdown-item" to="/profile">
                    <i class="bi bi-person me-2"></i>
                    Mi Perfil
                  </router-link>
                </li>
                <li>
                  <router-link class="dropdown-item" to="/settings">
                    <i class="bi bi-gear me-2"></i>
                    Configuración
                  </router-link>
                </li>
                <li><hr class="dropdown-divider"></li>
                <li>
                  <a class="dropdown-item text-danger" href="#" @click.prevent="handleLogout">
                    <i class="bi bi-box-arrow-right me-2"></i>
                    Cerrar Sesión
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="flex-grow-1">
      <slot />
    </main>

    <!-- Footer -->
    <footer class="navbar py-4 mt-5">
      <div class="container-fluid">
        <div class="row w-100">
          <div class="col-md-6">
            <small class="text-muted">
              &copy; 2025 MyGeotab Feeder Monitor v2.0 - Sistema de Gestión Vehicular
            </small>
          </div>
          <div class="col-md-6 text-end">
            <small class="text-muted">
              <i class="bi bi-clock me-1"></i>
              Última actualización: {{ currentTime }}
            </small>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useThemeStore } from '../stores/theme'
import { useAuthStore } from '../stores/auth'

export default {
  name: 'DefaultLayout',
  setup() {
    const router = useRouter()
    const currentTime = ref('')
    const themeStore = useThemeStore()
    const authStore = useAuthStore()
    if(!authStore.isAuthenticated){
      router.push('/login')
    }
    let timeInterval = null

    // Check if user is a client user (has client_id assigned)
    const isClientUser = computed(() => {
      return authStore.user?.client_id != null
    })

    // Update current time
    const updateTime = () => {
      currentTime.value = new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Theme toggle function
    const toggleTheme = () => {
      themeStore.toggleTheme()
    }

    // Logout function
    const handleLogout = async () => {
      try {
        await authStore.logout()
        router.push('/login')
      } catch (error) {
        console.error('Error during logout:', error)
      }
    }

    onMounted(() => {
      updateTime()
      timeInterval = setInterval(updateTime, 60000) // Update every minute
    })

    onUnmounted(() => {
      if (timeInterval) {
        clearInterval(timeInterval)
      }
    })

    return {
      currentTime,
      themeStore,
      authStore,
      isClientUser,
      toggleTheme,
      handleLogout
    }
  }
}
</script>

<style scoped>
/* Enhanced navbar brand */
.navbar-brand {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--primary-color), var(--info-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.navbar-brand:hover {
  transform: translateY(-1px);
  filter: brightness(1.1);
}

/* Status indicator animation */
.text-success {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}

/* Theme Toggle Button */
.nav-theme-toggle {
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 1rem;
  transition: var(--theme-transition);
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
}

.nav-theme-toggle:hover {
  background: var(--bg-elevated);
  color: var(--primary-color);
  border-color: var(--primary-alpha);
  transform: translateY(-1px);
  box-shadow: var(--glow-primary);
}

.nav-theme-toggle:active {
  transform: translateY(0);
}

.nav-theme-toggle i {
  font-size: 1rem;
  transition: var(--transition-fast);
}

/* Icon animations */
.nav-theme-toggle .bi-sun-fill {
  color: var(--warning-color);
  animation: rotate 0.5s ease-in-out;
}

.nav-theme-toggle .bi-moon-fill {
  color: var(--primary-color);
  animation: fadeIn 0.3s ease-in-out;
}

/* Animations for theme toggle */
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(180deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* User Dropdown */
.user-dropdown {
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 1rem;
  transition: var(--theme-transition);
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
}

.user-dropdown:hover {
  background: var(--bg-elevated);
  color: var(--primary-color);
  border-color: var(--primary-alpha);
  transform: translateY(-1px);
  box-shadow: var(--glow-primary);
}

.user-dropdown i {
  font-size: 1.25rem;
}

.dropdown-menu {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 250px;
  padding: 0.5rem;
}

.dropdown-header {
  padding: 0.75rem 1rem;
}

.user-info {
  text-align: left;
}

.user-info strong {
  color: var(--text-primary);
  font-size: 0.95rem;
}

.user-info small {
  font-size: 0.8rem;
}

.dropdown-item {
  color: var(--text-primary);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-sm);
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
}

.dropdown-item:hover {
  background: var(--bg-elevated);
  color: var(--primary-color);
}

.dropdown-item.text-danger:hover {
  background: rgba(220, 53, 69, 0.1);
  color: var(--danger-color);
}

.dropdown-divider {
  border-color: var(--border-color);
  margin: 0.5rem 0;
}

/* Mobile responsive navigation */
@media (max-width: 768px) {
  .navbar-brand {
    font-size: 1.2rem;
  }

  .navbar-nav .nav-link {
    text-align: center;
    padding: 0.75rem 1rem;
  }

  .nav-theme-toggle {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }

  .nav-theme-toggle span {
    display: none !important;
  }

  .nav-theme-toggle i {
    margin-right: 0 !important;
  }

  .user-dropdown {
    padding: 0.4rem 0.8rem;
  }

  .user-dropdown span {
    display: none !important;
  }
}
</style>
